import React from "react";
import { Session } from "../../model_gen/session";
import { EvaluateResult } from "../../model_gen/chat";
import { calculateScore, getSessionScoreColor } from "../Session/SessionUtils";
import PaginatedTable, { PaginatedTableColumn, SortConfig } from "../ui/paginated-table";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { CopyIcon, EyeIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";

interface SessionsTableProps {
  sessions: Session[];
  loading: boolean;
  sortConfig: SortConfig<Session>;
  onSort: (key: keyof Session) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  totalSessions: number;
  onSessionClick: (session: Session) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

const SessionsTable: React.FC<SessionsTableProps> = ({
  sessions,
  loading,
  sortConfig,
  onSort,
  currentPage,
  onPageChange,
  pageSize,
  totalSessions,
  onSessionClick,
  searchValue,
  onSearchChange,
}) => {
  const formatDate = (timestamp: number) => {
    if (timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const columns: PaginatedTableColumn<Session>[] = [
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[20%]",
      sortable: true,
      render: (session) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(session.date_created || 0)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(session.date_created || 0)}
          </div>
        </div>
      ),
    },
    {
      key: "session_id",
      header: "Session ID",
      width: "w-[25%]",
      render: (session) => (
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
            {session.session_id?.length > 12
              ? `${session.session_id.substring(0, 6)}...${session.session_id.substring(
                  session.session_id.length - 6
                )}`
              : session.session_id}
          </code>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(session.session_id || "");
            }}
          >
            <CopyIcon className="w-3 h-3" />
          </Button>
        </div>
      ),
    },
    {
      key: "date_ended",
      header: "Last Activity",
      width: "w-[15%]",
      sortable: true,
      render: (session) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {session.date_ended ? formatDate(session.date_ended) : formatDate(session.date_created || 0)}
          </div>
          <div className="text-xs text-muted-foreground">
            {session.date_ended ? formatTime(session.date_ended) : formatTime(session.date_created || 0)}
          </div>
        </div>
      ),
    },
    {
      key: "timeline",
      header: "Score",
      width: "w-[10%]",
      render: (session) => (
        <div className={cn(
          "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
          getSessionScoreColor(session) === "success" && "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
          getSessionScoreColor(session) === "error" && "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
          getSessionScoreColor(session) === "warning" && "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
          getSessionScoreColor(session) === "default" && "bg-muted text-foreground"
        )}>
          {calculateScore(session)}
        </div>
      ),
    },
    {
      key: "timeline",
      header: "Requests",
      width: "w-[10%]",
      render: (session) => (
        <span className="text-sm font-gc-med text-foreground">
          {session.timeline?.length || 0}
        </span>
      ),
    },
    {
      key: "timeline",
      header: "Faults",
      width: "w-[10%]",
      render: (session) => (
        <span className="text-sm font-gc-med text-foreground">
          {session.timeline?.reduce((acc, turn) => {
            const hasFault =
              turn.eval_request?.eval_results_set?.evaluation_results?.some(
                (evalResult: EvaluateResult) => evalResult.status === "FAULT"
              );
            return acc + (hasFault ? 1 : 0);
          }, 0) || 0}
        </span>
      ),
    },
    {
      key: "session_id",
      header: "Actions",
      width: "w-[10%]",
      render: (session) => (
        <Button
          variant="ghost"
          size="sm"
          className="gap-1"
          onClick={(e) => {
            e.stopPropagation();
            onSessionClick(session);
          }}
        >
          <EyeIcon className="w-3 h-3" />
          Details
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Sessions</h3>
          <p className="text-sm text-muted-foreground">View and manage application sessions</p>
        </div>
        {onSearchChange && (
          <Input
            placeholder="Search sessions..."
            className="w-80"
            value={searchValue || ""}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        )}
      </div>

      <PaginatedTable
        data={sessions}
        columns={columns}
        loading={loading}
        sortConfig={sortConfig}
        onSort={onSort}
        currentPage={currentPage}
        onPageChange={onPageChange}
        pageSize={pageSize}
        totalItems={totalSessions}
        emptyTitle="No sessions found"
        emptyDescription="No sessions have been created for this application yet."
        onRowClick={onSessionClick}
        serverSidePagination={true}
        defaultSort={(a, b) => {
          // Default sort by date_created descending
          const dateA = a.date_created && a.date_created > 0 ? a.date_created : 0;
          const dateB = b.date_created && b.date_created > 0 ? b.date_created : 0;
          return dateB - dateA;
        }}
      />
    </div>
  );
};

export default SessionsTable; 